import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Download, FileText, Image, FileVideo, Music, Archive, File, EyeOff, Clock, Edit3 } from 'lucide-react';
import { documentService } from '../../services/api';
import DocumentQA from './DocumentQA';
import VersionHistory from './VersionHistory';
import CollaboraEditor from './CollaboraEditor';
import useModal from '../../hooks/useModal';

const EDITABLE_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
]);

const DocumentPreview = ({ document, isOpen, onClose }) => {
  const navigate = useNavigate();
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  const fetchDownloadUrl = useCallback(async () => {
    if (!document?.id) return;

    try {
      setLoading(true);
      setError(null);
      // Get inline URL for preview (force_download=false)
      const response = await documentService.getDownloadUrl(document.id, false);
      setDownloadUrl(response.data.download_url);
    } catch (err) {
      console.error('Failed to get download URL:', err);
      setError('Failed to load document preview');
    } finally {
      setLoading(false);
    }
  }, [document]);

  useEffect(() => {
    if (isOpen && document) {
      fetchDownloadUrl();
    }
    return () => {
      setDownloadUrl(null);
      setError(null);
    };
  }, [isOpen, document, fetchDownloadUrl]);

  const getFileType = (mimeType, fileName) => {
    if (mimeType) {
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType === 'application/pdf') return 'pdf';
      if (mimeType.startsWith('video/')) return 'video';
      if (mimeType.startsWith('audio/')) return 'audio';
      if (mimeType.includes('text/')) return 'text';
    }
    
    // Fallback to file extension
    const extension = fileName?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';
    if (['mp4', 'avi', 'mov', 'webm'].includes(extension)) return 'video';
    if (['mp3', 'wav', 'ogg'].includes(extension)) return 'audio';
    if (['txt', 'md', 'csv'].includes(extension)) return 'text';
    if (['zip', 'rar', '7z', 'tar'].includes(extension)) return 'archive';
    
    return 'other';
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return Image;
      case 'pdf': return FileText;
      case 'video': return FileVideo;
      case 'audio': return Music;
      case 'archive': return Archive;
      default: return File;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownload = async () => {
    if (!document?.id) return;

    try {
      // Fetch download URL with force_download=true
      const response = await documentService.getDownloadUrl(document.id, true);
      window.open(response.data.download_url, '_blank');
    } catch (err) {
      console.error('Failed to download file:', err);
    }
  };

  if (!isOpen || !document) return null;

  const fileType = getFileType(document.mime_type, document.original_file_name);
  const FileIcon = getFileIcon(fileType);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <FileIcon className="h-6 w-6 text-gray-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 break-words" title={document.original_file_name}>
                {document.original_file_name}
              </h2>
              <p className="text-sm text-gray-500">
                {formatFileSize(document.file_size)} • {document.mime_type}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            <button
              onClick={() => setShowVersionHistory(true)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Version History"
            >
              <Clock className="h-5 w-5" />
            </button>
            {EDITABLE_MIME_TYPES.has(document.mime_type) && (
              <button
                onClick={() => setShowEditor(true)}
                className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit in Browser"
              >
                <Edit3 className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => {
                onClose();
                navigate(`/documents/${document.id}/redact`);
              }}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Redact Document"
            >
              <EyeOff className="h-5 w-5" />
            </button>
            <button
              onClick={handleDownload}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Download"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
              <span className="ml-2 text-gray-600">Loading preview...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64">
              <FileIcon className="h-16 w-16 text-gray-400 mb-4" />
              <p className="text-red-600 mb-2">{error}</p>
              <button
                onClick={fetchDownloadUrl}
                className="text-yellow-600 hover:text-gray-800 text-sm"
              >
                Try again
              </button>
            </div>
          ) : !downloadUrl ? (
            <div className="flex items-center justify-center h-64">
              <FileIcon className="h-16 w-16 text-gray-400 mb-4" />
              <p className="text-gray-500">Unable to generate preview URL</p>
            </div>
          ) : (
            <div className="p-4">
              {fileType === 'image' && (
                <div className="flex justify-center">
                  <img
                    src={downloadUrl}
                    alt={document.original_file_name}
                    className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                    onError={(e) => setError('Failed to load image')}
                  />
                </div>
              )}

              {fileType === 'pdf' && (
                <div className="w-full h-[60vh]">
                  <iframe
                    src={`${downloadUrl}#toolbar=0`}
                    title={document.original_file_name}
                    className="w-full h-full border rounded-lg"
                    onError={(e) => setError('Failed to load PDF')}
                  />
                </div>
              )}

              {fileType === 'video' && (
                <div className="flex justify-center">
                  <video
                    controls
                    className="max-w-full max-h-[60vh] rounded-lg shadow-lg"
                    onError={(e) => setError('Failed to load video')}
                  >
                    <source src={downloadUrl} type={document.mime_type} />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}

              {fileType === 'audio' && (
                <div className="flex flex-col items-center justify-center h-64">
                  <Music className="h-16 w-16 text-gray-400 mb-4" />
                  <audio
                    controls
                    className="w-full max-w-md"
                    onError={(e) => setError('Failed to load audio')}
                  >
                    <source src={downloadUrl} type={document.mime_type} />
                    Your browser does not support the audio tag.
                  </audio>
                </div>
              )}

              {fileType === 'text' && (
                <div className="bg-gray-50 rounded-lg p-4 max-h-[60vh] overflow-auto">
                  <iframe
                    src={downloadUrl}
                    title={document.original_file_name}
                    className="w-full h-[50vh] border-0"
                    onError={(e) => setError('Failed to load text file')}
                  />
                </div>
              )}

              {['archive', 'other'].includes(fileType) && (
                <div className="flex flex-col items-center justify-center h-64">
                  <FileIcon className="h-16 w-16 text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4 text-center">
                    Preview not available for this file type
                  </p>
                  <button
                    onClick={handleDownload}
                    className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download to View
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Document Q&A Section */}
          <div className="border-t border-gray-200">
            <DocumentQA documentId={document.id} />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div>
              Uploaded {new Date(document.created_at).toLocaleDateString()} 
              {document.uploaded_by_name && ` by ${document.uploaded_by_name}`}
            </div>
            {document.document_type && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {document.document_type.replace('_', ' ')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Version History Panel */}
      <VersionHistory
        document={document}
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        onRestore={() => fetchDownloadUrl()}
      />

      {/* Collabora Editor Modal */}
      <CollaboraEditor
        documentId={document?.id}
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={() => fetchDownloadUrl()}
      />
    </div>
  );
};

export default DocumentPreview;
